// Live course search backed by golfcourseapi.com — the same provider the
// GolfGamesScorecard mobile app falls back to when a course isn't in its
// bundled NCRDB. Web build can't ship the 8 MB local DB, so all lookups
// hit the remote API.
//
// The director only needs to find their tournament's home course and
// pick the tee box being played that day. The selected tee gives us
// slope + course rating + par which feed the full USGA WHS course
// handicap formula.

const API_KEY  = 'XU3NQMA5YXTFIM3LOSVU6CB3J4'
const BASE_URL = 'https://api.golfcourseapi.com/v1'

// Normalize a raw tee object from the API into our TeeBox shape.
// `par_total` is the per-tee par the USGA formula needs; some API rows
// have it on the tee directly, some only on the course total — caller
// supplies a fallback in that case.
function normalizeTee(raw, fallbackPar) {
  const name   = String(raw?.tee_name || raw?.tee_color || '').trim()
  const rating = Number(raw?.course_rating) || 0
  const slope  = Number(raw?.slope_rating)  || 0
  const par    = Number(raw?.par_total) || Number(fallbackPar) || 72
  if (!name || rating <= 0 || slope <= 0) return null
  return { name, rating, slope, par }
}

function dedupeTees(tees) {
  const seen = new Set()
  const out = []
  for (const t of tees) {
    const k = `${t.name}|${t.rating}|${t.slope}|${t.par}`
    if (seen.has(k)) continue
    seen.add(k)
    out.push(t)
  }
  return out
}

// Pull both male and female tee lists from the course shape into a
// single flat array. Per-tee par fills in from `par_total` when present.
function teesFromCourse(c) {
  const fallbackPar = Number(c?.par_total) || 72
  const all = []
  for (const bucket of [c?.tees?.male || [], c?.tees?.female || []]) {
    for (const t of bucket) {
      const norm = normalizeTee(t, fallbackPar)
      if (norm) all.push(norm)
    }
  }
  return dedupeTees(all)
}

// Per-hole pars + stroke index (handicap) from a raw API course. Reads the
// first tee that carries a full 18-hole `holes` array (pars/SI are the same
// across tees). Returns null when the course has no usable per-hole data.
// Additive helper — existing callers are unaffected.
export function holesFromRawCourse(c) {
  for (const bucket of [c?.tees?.male || [], c?.tees?.female || []]) {
    for (const t of bucket) {
      const holes = Array.isArray(t?.holes) ? t.holes : []
      if (holes.length === 18) {
        const pars = holes.map((h) => Number(h?.par) || null)
        const strokeIndex = holes.map((h) => Number(h?.handicap) || null)
        if (pars.every((p) => p) && strokeIndex.every((s) => s)) return { pars, strokeIndex }
      }
    }
  }
  return null
}

/**
 * Search the course catalog by name. Returns up to ~20 best matches with
 * the full per-course payload attached so callers don't need a second
 * roundtrip for tee data. Empty array on short queries / API failure.
 * Each result also carries per-hole `pars` + `strokeIndex` when available
 * (additive fields; older callers ignore them).
 */
export async function searchCourses(query) {
  const q = (query || '').trim()
  if (q.length < 3) return []
  try {
    const url = `${BASE_URL}/search?search_query=${encodeURIComponent(q)}`
    const r = await fetch(url, {
      headers: { 'Authorization': `Key ${API_KEY}` },
    })
    if (!r.ok) return []
    const j = await r.json()
    const courses = Array.isArray(j?.courses) ? j.courses : []
    return courses.map((c) => {
      const teeBoxes = teesFromCourse(c)
      // Course total par: pick the most common tee par when available,
      // else the API's top-level par_total, else 72.
      const teePars = teeBoxes.map((t) => t.par).filter(Boolean)
      const par = teePars[0] || Number(c?.par_total) || 72
      const holes = holesFromRawCourse(c)
      return {
        id: String(c.id),
        name: String(c.course_name || ''),
        location: [c.location?.city, c.location?.state].filter(Boolean).join(', ')
          || String(c.club_name || ''),
        par,
        teeBoxes,
        pars: holes?.pars,
        strokeIndex: holes?.strokeIndex,
      }
    })
  } catch {
    return []
  }
}
